import type { ArrivalClaim, RunStatus } from "@wayfind/core";
import { ageWords, basisWords, positionWords, profileWords } from "../words";

/**
 * Where the number came from, in three lines.
 *
 * Every tracking product in this category shows a time and stops. This
 * shows a time and then says what it rests on, because §12 is explicit
 * that "bus expected 07:44" and "bus is here, arriving 07:44" are
 * different claims — and the difference is the entire reason anyone opened
 * the link. It is small, quiet type; it is not a warning, it is a receipt.
 */
export function Provenance({
  status,
  arrival
}: {
  status: RunStatus;
  arrival: ArrivalClaim;
}): JSX.Element {
  const profile = profileWords(arrival);
  return (
    <div className="panel">
      <p className="eyebrow">What this is based on</p>
      <div className="provenance">
        <div className="provenance__row">
          <span className="provenance__key">Arrival</span>
          <p className="provenance__value">{basisWords(arrival)}</p>
        </div>
        <div className="provenance__row">
          <span className="provenance__key">Position</span>
          <p className="provenance__value">{positionWords(status.position)}</p>
        </div>
        <div className="provenance__row">
          <span className="provenance__key">Last heard</span>
          <p className="provenance__value">
            {status.observed.ageSeconds == null
              ? "Nothing yet."
              : ageWords(status.observed.ageSeconds)}
            {status.observed.stale ? " — too long ago to call live." : ""}
          </p>
        </div>
        {profile ? (
          <div className="provenance__row">
            <span className="provenance__key">Journey</span>
            <p className="provenance__value">{profile}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The short chips above the fold: state at a glance, and nothing louder. */
export function Chips({
  status,
  arrival
}: {
  status: RunStatus;
  arrival: ArrivalClaim;
}): JSX.Element {
  const chips: { key: string; label: string; tone: string }[] = [];

  if (status.position.kind === "live") {
    chips.push({ key: "live", label: "Live position", tone: "chip--live" });
  } else if (status.position.kind === "withheld") {
    chips.push({ key: "coarse", label: "Stops only", tone: "" });
  } else if (status.position.kind === "stale") {
    chips.push({ key: "stale", label: "Position out of date", tone: "" });
  } else if (status.position.kind === "unplaceable") {
    chips.push({ key: "nomap", label: "No map here", tone: "" });
  }

  if (arrival.kind === "estimate") {
    chips.push({
      key: "metric",
      label: arrival.metric === "live-traffic" ? "Live traffic" : "Map speeds",
      tone: arrival.metric === "live-traffic" ? "chip--live" : ""
    });
  }
  if (arrival.kind === "scheduled") chips.push({ key: "sched", label: "Timetable", tone: "" });
  if (arrival.kind === "served") chips.push({ key: "done", label: "Delivered", tone: "chip--settled" });
  if (arrival.kind === "not-coming") {
    chips.push({ key: "halt", label: "Not coming", tone: "chip--halt" });
  }
  if (status.lastOutcome?.hasPhoto) {
    chips.push({ key: "photo", label: "Photo attached", tone: "" });
  }

  return (
    <ul className="chips">
      {chips.map(chip => (
        <li key={chip.key} className={`chip ${chip.tone}`}>
          <span className="chip__dot" aria-hidden="true" />
          {chip.label}
        </li>
      ))}
    </ul>
  );
}
